import { NotificationService, type WebPushSubscriptionPayload } from '@/services/notification.service';
import { useEffect, useState } from 'react';

export type PushNotificationStatus = 'unsupported' | 'prompt' | 'denied' | 'subscribed';

function isPushSupported() {
  return (
    typeof window !== 'undefined' && 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window
  );
}

function urlBase64ToUint8Array(value: string) {
  const padding = '='.repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, '+').replace(/_/g, '/');
  const decoded = window.atob(base64);
  return Uint8Array.from(decoded, (character) => character.charCodeAt(0));
}

function serializeSubscription(subscription: PushSubscription): WebPushSubscriptionPayload {
  const json = subscription.toJSON();
  const p256dh = json.keys?.p256dh;
  const auth = json.keys?.auth;

  if (!p256dh || !auth) {
    throw new Error('O navegador não retornou as chaves da inscrição Push.');
  }

  return {
    endpoint: subscription.endpoint,
    expirationTime: subscription.expirationTime,
    keys: { p256dh, auth },
  };
}

export function usePushNotifications() {
  const supported = isPushSupported();
  const [status, setStatus] = useState<PushNotificationStatus>(() => {
    if (!supported) return 'unsupported';
    return Notification.permission === 'denied' ? 'denied' : 'prompt';
  });
  const [isPending, setIsPending] = useState(false);

  useEffect(() => {
    if (!supported || Notification.permission !== 'granted') return;
    let cancelled = false;

    void navigator.serviceWorker.ready
      .then((registration) => registration.pushManager.getSubscription())
      .then(async (subscription) => {
        if (!subscription) return;
        await NotificationService.registerWebPushSubscription(serializeSubscription(subscription));
        if (!cancelled) setStatus('subscribed');
      })
      .catch(() => {
        if (!cancelled) setStatus('prompt');
      });

    return () => {
      cancelled = true;
    };
  }, [supported]);

  const enable = async () => {
    if (!supported) return false;
    setIsPending(true);

    try {
      const permission = Notification.permission === 'granted' ? 'granted' : await Notification.requestPermission();

      if (permission !== 'granted') {
        setStatus(permission === 'denied' ? 'denied' : 'prompt');
        return false;
      }

      const { publicKey } = await NotificationService.getWebPushPublicKey();
      if (!publicKey) throw new Error('As notificações em tempo real ainda não estão configuradas.');

      const registration = await navigator.serviceWorker.ready;
      const existingSubscription = await registration.pushManager.getSubscription();
      const subscription =
        existingSubscription ??
        (await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        }));

      await NotificationService.registerWebPushSubscription(serializeSubscription(subscription));
      setStatus('subscribed');
      return true;
    } finally {
      setIsPending(false);
    }
  };

  const disable = async () => {
    if (!supported) return;
    setIsPending(true);

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        await NotificationService.unregisterWebPushSubscription(subscription.endpoint);
        await subscription.unsubscribe();
      }
      setStatus(Notification.permission === 'denied' ? 'denied' : 'prompt');
    } finally {
      setIsPending(false);
    }
  };

  return { status, isPending, enable, disable };
}
